import React, { useState, useEffect } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, Trash, PaperPlaneTilt, Repeat, CalendarBlank } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function RecurringInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    description: '',
    amount: '',
    frequency: 'monthly',
    next_send_date: new Date(),
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        api.get('/recurring-invoices'),
        api.get('/clients'),
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { data } = await api.post('/recurring-invoices', {
        ...formData,
        amount: parseFloat(formData.amount),
        next_send_date: format(formData.next_send_date, 'yyyy-MM-dd'),
      });
      setInvoices([data, ...invoices]);
      setDialogOpen(false);
      setFormData({
        client_id: '',
        description: '',
        amount: '',
        frequency: 'monthly',
        next_send_date: new Date(),
      });
      toast.success('Recurring invoice created');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async (id) => {
    setSending(id);
    try {
      const { data } = await api.post(`/recurring-invoices/${id}/send`);
      toast.success(data.message);
      if (data.note) {
        toast.info(data.note);
      }
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring invoice?')) return;

    try {
      await api.delete(`/recurring-invoices/${id}`);
      setInvoices(invoices.filter((inv) => inv.id !== id));
      toast.success('Recurring invoice deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="recurring-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Recurring Invoices</h1>
          <p className="text-muted-foreground">Set up and manage automatic monthly invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-recurring-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Recurring Invoice
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Recurring Invoice</DialogTitle>
              <DialogDescription>Set up a new recurring invoice for a client</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger data-testid="recurring-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Monthly accounting services"
                  required
                  data-testid="recurring-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="recurring-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                  >
                    <SelectTrigger data-testid="recurring-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Next Send Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="recurring-date-picker"
                    >
                      <CalendarBlank className="w-4 h-4 mr-2" />
                      {formData.next_send_date ? format(formData.next_send_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.next_send_date}
                      onSelect={(date) => setFormData({ ...formData, next_send_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || !formData.client_id} data-testid="recurring-submit-btn">
                  {submitting ? 'Creating...' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Invoices Table */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4 flex items-center gap-2">
            <Repeat className="w-5 h-5" />
            Active Recurring Invoices
          </CardTitle>
          <CardDescription>{invoices.length} recurring invoices configured</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <Repeat className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No recurring invoices set up</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first recurring invoice to automate billing
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Client</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Next Send</TableHead>
                  <TableHead>Times Sent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`recurring-row-${invoice.id}`}>
                    <TableCell className="font-medium">{getClientName(invoice.client_id)}</TableCell>
                    <TableCell className="max-w-xs truncate">{invoice.description}</TableCell>
                    <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                    <TableCell className="capitalize">{invoice.frequency}</TableCell>
                    <TableCell>{invoice.next_send_date}</TableCell>
                    <TableCell>{invoice.send_count || 0}</TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={invoice.status === 'active' ? 'status-onboarded' : 'status-pending'}
                      >
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSend(invoice.id)}
                          disabled={sending === invoice.id}
                          data-testid={`send-recurring-${invoice.id}`}
                        >
                          {sending === invoice.id ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <PaperPlaneTilt className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(invoice.id)}
                          data-testid={`delete-recurring-${invoice.id}`}
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Note about email */}
      <Card className="border border-border bg-muted/50">
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Email delivery requires SendGrid API key configuration. 
            Currently, invoices are logged but not sent via email. Contact your administrator to set up email integration.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
